/**
 * Terms — a readable beta agreement in the Residence marketing language.
 */
import {
  MarketingLayout, Eyebrow, Display, SectionTitle, FocalSentence,
  Body, Section, Hairline, SecondaryLink,
} from "./MarketingLayout";

function Block({ title, children, testid }) {
  return (
    <div className="max-w-[62ch]" data-testid={testid}>
      <SectionTitle>{title}</SectionTitle>
      <div className="mt-4 space-y-4">{children}</div>
    </div>
  );
}

export default function TermsPage() {
  return (
    <MarketingLayout testid="terms-page">
      <Section testid="terms-hero" className="pt-24 sm:pt-32 pb-14">
        <div className="max-w-[820px]">
          <Eyebrow>Terms</Eyebrow>
          <Display className="mt-6">The agreement, in plain words.</Display>
          <FocalSentence className="mt-8 max-w-[46ch]">
            No fine print designed to be skipped. This is what you're agreeing to when you use ARA.
          </FocalSentence>
          <Body className="mt-6" style={{ color: 'var(--ara-ink-soft, #7A7772)' }}>
            Last updated June 2026.
          </Body>
        </div>
      </Section>

      <Hairline />

      <Section testid="terms-body" className="py-16 space-y-14">
        <Block title="ARA is in private beta" testid="terms-beta">
          <Body>
            You're using an early version of ARA. Things will occasionally change, improve
            or be temporarily imperfect. We'll always try to warn you before anything
            meaningful changes, and Founding Member pricing is honoured once offered.
          </Body>
        </Block>

        <Block title="Your account" testid="terms-account">
          <Body>
            You're responsible for keeping your sign-in details private and for what
            happens under your account. Add only people who belong to your household, and
            tell us straight away if you think someone else has access.
          </Body>
        </Block>

        <Block title="Acceptable use" testid="terms-acceptable-use">
          <Body>
            Use ARA for your own household. Don't attempt to break, probe or overload the
            service, access other households' information, or use ARA for anything unlawful.
          </Body>
        </Block>

        <Block title="Money information, not money advice" testid="terms-financial">
          <Body>
            The Study organises your household's financial information. It is not
            financial advice, and ARA is not a financial adviser. Decisions about your
            money remain yours. Bank connections are read-only — ARA cannot move money
            or make payments.
          </Body>
        </Block>

        <Block title="Availability" testid="terms-availability">
          <Body>
            We work to keep ARA available and reliable, but as a beta service we can't
            promise it will be uninterrupted or error-free. We'll communicate honestly
            when something goes wrong.
          </Body>
        </Block>

        <Block title="Your content and ours" testid="terms-ip">
          <Body>
            Everything you put into your Residence stays yours. The ARA software, design
            and name stay ours. You give us only the permission needed to store and show
            your information back to your household.
          </Body>
        </Block>

        <Block title="Leaving" testid="terms-termination">
          <Body>
            You can close your account whenever you like and ask for your information to
            be deleted. We may suspend accounts that put other households or the service
            at risk, and we'll explain why if we ever have to.
          </Body>
        </Block>

        <Block title="Changes to these terms" testid="terms-changes">
          <Body>
            If these terms change in any way that matters, we'll tell you by email before
            the change takes effect.
          </Body>
        </Block>

        <Block title="Contact" testid="terms-contact">
          <Body>
            Questions about any of this? <SecondaryLink to="/contact">Get in touch</SecondaryLink> —
            a person will reply.
          </Body>
          <Body style={{ color: 'var(--ara-ink-soft, #7A7772)' }}>
            This plain-English agreement will be reviewed by a New Zealand legal
            professional before ARA leaves beta.
          </Body>
        </Block>
      </Section>
    </MarketingLayout>
  );
}
